import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiUrl } from '../../services/apiClient';
import { WORKS_CATEGORIES, WorksCategory, WorksItem, fetchWorks, worksCategoryLabel } from './worksService';
import styles from './Works.module.scss';

function Works() {
  const [items, setItems] = useState<WorksItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // 헤더 서브메뉴(/works?cat=branding)에서 바로 분야를 고를 수 있도록 선택한 분야는 URL에 둔다
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get('cat');
  const filter: WorksCategory | 'all' = WORKS_CATEGORIES.some((c) => c.id === catParam)
    ? (catParam as WorksCategory)
    : 'all';
  const setFilter = (next: WorksCategory | 'all') => setSearchParams(next === 'all' ? {} : { cat: next });
  const [selected, setSelected] = useState<WorksItem | null>(null);

  useEffect(() => {
    fetchWorks()
      .then(setItems)
      .catch((err) => setLoadError(err instanceof Error ? err.message : '포트폴리오를 불러오지 못했습니다.'));
  }, []);

  // 확대 보기가 열려 있는 동안 ESC로 닫는다
  useEffect(() => {
    if (!selected) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selected]);

  const visibleItems = items?.filter((item) => filter === 'all' || item.category === filter) ?? [];

  return (
    <div className={styles.page}>
      {/* PAGE HEAD */}
      <section className={styles.pageHead}>
        <div className={styles.crumb}>
          <Link to="/">HOME</Link>
          <svg viewBox="0 0 24 24">
            <path d="m9 18 6-6-6-6" />
          </svg>
          <span className={styles.crumbHere}>포트폴리오</span>
        </div>
        <span className={styles.eyebrow}>Portfolio</span>
        <h1 className={styles.pageTitle}>
          <em>포트폴리오</em>
        </h1>
        <p className={styles.pageDesc}>G-WORKS가 작업한 디자인 사례를 소개합니다.</p>
      </section>

      {/* LAYOUT */}
      <section className={styles.layout}>
        {loadError && (
          <div className={styles.empty}>
            <strong>포트폴리오를 불러오지 못했습니다</strong>
            {loadError}
          </div>
        )}

        {!loadError && (
          <>
            <div className={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={filter === 'all'}
                className={filter === 'all' ? styles.tabActive : ''}
                onClick={() => setFilter('all')}
              >
                전체
              </button>
              {WORKS_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === category.id}
                  className={filter === category.id ? styles.tabActive : ''}
                  onClick={() => setFilter(category.id)}
                >
                  {category.label}
                </button>
              ))}
            </div>

            {items && visibleItems.length === 0 && (
              <div className={styles.empty}>
                <strong>준비 중입니다</strong>
                곧 새로운 포트폴리오로 찾아뵙겠습니다.
              </div>
            )}

            {visibleItems.length > 0 && (
              <ul className={styles.grid}>
                {visibleItems.map((item) => (
                  <li key={item.id}>
                    <button type="button" className={styles.card} onClick={() => setSelected(item)}>
                      <span className={styles.thumb}>
                        <img src={apiUrl(item.imageUrl)} alt={item.title} loading="lazy" />
                      </span>
                      <span className={styles.cardTag}>{worksCategoryLabel(item.category)}</span>
                      <strong className={styles.cardTitle}>{item.title}</strong>
                      {item.clientName && <span className={styles.cardClient}>{item.clientName}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {selected && (
        <div className={styles.modalBackdrop} onClick={() => setSelected(null)}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-label={selected.title}
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className={styles.modalClose} aria-label="닫기" onClick={() => setSelected(null)}>
              ×
            </button>
            <img src={apiUrl(selected.imageUrl)} alt={selected.title} className={styles.modalImage} />
            <div className={styles.modalBody}>
              <span className={styles.cardTag}>{worksCategoryLabel(selected.category)}</span>
              <h2>{selected.title}</h2>
              {selected.clientName && <p className={styles.cardClient}>{selected.clientName}</p>}
              {selected.description && <p className={styles.modalDesc}>{selected.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Works;
